// Lucka 2: "Dagslage vid datum" - en ogonblicksbild av projektets tillstand vid ett valt datum.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import DocumentHeader from "./DocumentHeader";
import {
  History,
  CalendarDays,
  FileText,
  ShieldAlert,
  Wrench,
  ClipboardCheck,
  AlertTriangle,
  Download,
} from "lucide-react";

interface Props {
  projectId: string;
}

interface SnapshotData {
  date: string;
  ampVersions: { version: number; status: string; revision_note: string | null; created_at: string }[];
  risks: { id: string; type: string | null; name: string; updated_at: string | null }[];
  workPreps: { id: string; name: string; status: string; updated_at: string }[];
  competences: { id: string; person_name: string | null; competence_type: string; valid_until: string | null }[];
  incidents: { id: string; description: string; status: string; occurred_at: string | null }[];
  safetyRounds: { id: string; name: string; round_date: string | null; status: string }[];
  equipment: { id: string; object_name: string; next_inspection: string | null; last_inspected: string | null }[];
}

const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString("sv-SE") : "–");

const StatusSnapshot: React.FC<Props> = ({ projectId }) => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [snapshot, setSnapshot] = useState<SnapshotData | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const generate = useCallback(async () => {
    setLoading(true);
    const targetDate = new Date(date);
    targetDate.setHours(23, 59, 59, 999);
    const isoEnd = targetDate.toISOString();

    const [ampRes, riskRes, prepRes, compRes, incRes, roundRes, equipRes] = await Promise.all([
      supabase
        .from("amp_versions")
        .select("version, status, revision_note, created_at")
        .eq("project_id", projectId)
        .lte("created_at", isoEnd)
        .order("version", { ascending: false })
        .limit(5),
      supabase
        .from("folder_risk_assessments")
        .select("id, type, name, updated_at")
        .eq("project_id", projectId)
        .is("deleted_at", null)
        .lte("created_at", isoEnd),
      supabase
        .from("work_preparations")
        .select("id, name, status, updated_at")
        .eq("project_id", projectId)
        .is("deleted_at", null)
        .lte("created_at", isoEnd),
      supabase
        .from("contractor_competences")
        .select("id, person_name, competence_type, valid_until")
        .eq("project_id", projectId),
      supabase
        .from("incident_reports")
        .select("id, description, status, occurred_at")
        .eq("project_id", projectId)
        .lte("created_at", isoEnd),
      supabase
        .from("safety_rounds")
        .select("id, name, round_date, status")
        .eq("project_id", projectId)
        .lte("created_at", isoEnd),
      supabase
        .from("equipment_inspections")
        .select("id, object_name, next_inspection, last_inspected")
        .eq("project_id", projectId)
        .eq("status", "active"),
    ]);

    if (ampRes.error || riskRes.error || prepRes.error || compRes.error || incRes.error || roundRes.error || equipRes.error) {
      toast.error("Kunde inte hämta projektdata för rapporten");
      console.error("snapshot errors", ampRes.error, riskRes.error, prepRes.error);
      setLoading(false);
      return;
    }

    const validCompetences = (compRes.data || []).filter((c) => {
      if (!c.valid_until) return true;
      return new Date(c.valid_until) >= new Date(date);
    });
    const expiredCompetences = (compRes.data || []).filter((c) => {
      if (!c.valid_until) return false;
      return new Date(c.valid_until) < new Date(date);
    });
    const openIncidents = (incRes.data || []).filter((i) => i.status !== "closed");
    const overdueEquipment = (equipRes.data || []).filter((e) => {
      if (!e.next_inspection) return false;
      return new Date(e.next_inspection) < new Date(date);
    });

    setSnapshot({
      date,
      ampVersions: (ampRes.data || []) as SnapshotData["ampVersions"],
      risks: (riskRes.data || []) as SnapshotData["risks"],
      workPreps: (prepRes.data || []) as SnapshotData["workPreps"],
      competences: validCompetences as SnapshotData["competences"],
      incidents: openIncidents as SnapshotData["incidents"],
      safetyRounds: (roundRes.data || []) as SnapshotData["safetyRounds"],
      equipment: overdueEquipment as SnapshotData["equipment"],
    });

    // Lagga en sakerhetslog for rapporten
    const { data: auth } = await supabase.auth.getUser();
    await supabase.from("security_audit_log").insert({
      action_type: "status_snapshot_generated",
      resource_type: "project",
      resource_id: projectId,
      details: { date, generated_by: auth.user?.id ?? null },
    });

    toast.success(`Dagsläge för ${fmt(date)} genererat`);
    setLoading(false);
  }, [projectId, date]);

  useEffect(() => {
    // Generera inte automatiskt - anvandaren valjer datum och klickar
  }, []);

  const exportWord = useCallback(async () => {
    if (!snapshot) return;
    setExporting(true);
    try {
      const { data: proj } = await supabase
        .from("projects")
        .select("name")
        .eq("id", projectId)
        .maybeSingle();
      const { generateStatusSnapshotWordDocument } = await import(
        "@/lib/documentGenerator/statusSnapshotWordGenerator"
      );
      await generateStatusSnapshotWordDocument(snapshot, proj?.name ?? undefined);
      toast.success("Dagslägesrapporten är nedladdad");
    } catch (e) {
      console.error(e);
      toast.error("Kunde inte skapa Word-dokumentet");
    } finally {
      setExporting(false);
    }
  }, [snapshot, projectId]);

  const activeAmp = snapshot?.ampVersions[0];


  return (
    <div className="space-y-4">
      <DocumentHeader
        icon={History}
        docNumber="06c"
        title="Dagsläge vid datum"
        description="Frys projektets tillstånd vid ett valt datum. Visar vilken AMP-version som gällde, vilka risker som var öppna, vilka bevis som gällde och vilka besiktningar som var förfallna. Används vid utredningar och tillsyn i efterhand."
        legalRef="AFS 2023:3 kap. 6"
        meta={snapshot && <Badge variant="secondary">{fmt(snapshot.date)}</Badge>}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Välj datum</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div>
            <Label>Datum för dagsläget</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full sm:w-[200px]"
            />
          </div>
          <Button onClick={generate} disabled={loading}>
            <CalendarDays className="mr-1 h-4 w-4" />
            {loading ? "Genererar…" : "Generera rapport"}
          </Button>
          <Button variant="outline" onClick={exportWord} disabled={!snapshot || exporting}>
            <Download className="mr-1 h-4 w-4" />
            {exporting ? "Exporterar…" : "Ladda ner som Word"}
          </Button>

        </CardContent>
      </Card>

      {snapshot && (
        <div className="space-y-3">
          {/* AMP-version */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4 text-primary" />
                Arbetsmiljöplan vid datumet
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeAmp ? (
                <div className="space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Aktiv version:</span> v{activeAmp.version}{" "}
                    <Badge variant="secondary" className="ml-2">{activeAmp.status}</Badge>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Fryst {fmt(activeAmp.created_at)}
                    {activeAmp.revision_note ? ` · ${activeAmp.revision_note}` : ""}
                  </p>
                  {snapshot.ampVersions.length > 1 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {snapshot.ampVersions.length} tidigare versioner funna vid detta datum.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Ingen fryst AMP-version fanns vid detta datum.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Risker */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldAlert className="h-4 w-4 text-primary" />
                Risksammanställningar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                <span className="font-medium">{snapshot.risks.length}</span> risksammanställningar
                fanns registrerade vid datumet.
              </p>
              {snapshot.risks.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {snapshot.risks.slice(0, 5).map((r) => (
                    <li key={r.id}>
                      {r.name || r.type || "Risk"} · uppdaterad {fmt(r.updated_at)}
                    </li>
                  ))}
                  {snapshot.risks.length > 5 && (
                    <li>…och {snapshot.risks.length - 5} till</li>
                  )}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Arbetsberedningar */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Wrench className="h-4 w-4 text-primary" />
                Arbetsberedningar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                <span className="font-medium">{snapshot.workPreps.length}</span> arbetsberedningar
                fanns registrerade vid datumet.
              </p>
              {snapshot.workPreps.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {snapshot.workPreps.slice(0, 5).map((p) => (
                    <li key={p.id}>
                      {p.name} · status: {p.status}
                    </li>
                  ))}
                  {snapshot.workPreps.length > 5 && (
                    <li>…och {snapshot.workPreps.length - 5} till</li>
                  )}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Kompetensbevis */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ClipboardCheck className="h-4 w-4 text-primary" />
                Behörighetsbevis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                <span className="font-medium text-emerald-600">{snapshot.competences.length}</span> bevis
                giltiga vid datumet.
              </p>
              {snapshot.competences.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {snapshot.competences.slice(0, 5).map((c) => (
                    <li key={c.id}>
                      {c.person_name || "Okänd"} – {c.competence_type}
                      {c.valid_until ? ` (giltigt till ${fmt(c.valid_until)})` : ""}
                    </li>
                  ))}
                  {snapshot.competences.length > 5 && (
                    <li>…och {snapshot.competences.length - 5} till</li>
                  )}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Tillbud */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-4 w-4 text-primary" />
                Öppna tillbud/olyckor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                <span className="font-medium text-amber-600">{snapshot.incidents.length}</span> öppna
                tillbud/olyckor vid datumet.
              </p>
              {snapshot.incidents.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {snapshot.incidents.slice(0, 3).map((i) => (
                    <li key={i.id}>
                      {String(i.description || "").slice(0, 80) || "Ingen beskrivning"} · status: {i.status}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Utrustningsbesiktningar */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Wrench className="h-4 w-4 text-primary" />
                Förfallna besiktningar
              </CardTitle>
            </CardHeader>
            <CardContent>
              {snapshot.equipment.length > 0 ? (
                <div className="space-y-1">
                  <p className="text-sm">
                    <span className="font-medium text-destructive">{snapshot.equipment.length}</span> besiktningar
                    var förfallna vid datumet:
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {snapshot.equipment.map((e) => (
                      <li key={e.id}>
                        {e.object_name} – senast besiktigad {fmt(e.last_inspected)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Inga förfallna besiktningar vid detta datum.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Skyddsronder */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ClipboardCheck className="h-4 w-4 text-primary" />
                Skyddsronder
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                <span className="font-medium">{snapshot.safetyRounds.length}</span> skyddsronder
                genomförda vid datumet.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default StatusSnapshot;
